/**
 * CoachSchedule — Calendar component for coaches showing sessions.
 * Week view (default) and Month view with session badges.
 */
import { useState, useEffect, useCallback } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, ScrollView,
  Dimensions, Modal, Pressable, Alert,
} from 'react-native';
import {
  ChevronLeft, ChevronRight, CalendarDays, LayoutGrid,
  Clock, User, X, Trash2, Edit3,
} from 'lucide-react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { TYPO } from '@/constants/typography';
import { FONT, RADIUS, SPACE } from '@/constants/tokens';
import { getSessionsForWeek, getSessions, cancelSession, type CoachSession } from '@/lib/coachSchedule';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

interface Props {
  coachId: string;
  onEditSession?: (session: CoachSession) => void;
  refreshKey?: number;
}

type ViewMode = 'week' | 'month';

function getWeekStart(date: Date): Date {
  const d = new Date(date);
  d.setDate(d.getDate() - d.getDay());
  d.setHours(0, 0, 0, 0);
  return d;
}

function formatDateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatTime12(time24: string): string {
  const [hStr, mStr] = time24.split(':');
  const h = parseInt(hStr, 10);
  const m = mStr || '00';
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${m} ${ampm}`;
}

export default function CoachSchedule({ coachId, onEditSession, refreshKey }: Props) {
  const { theme } = useTheme();
  const c = theme.colors;

  const [viewMode, setViewMode] = useState<ViewMode>('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [sessions, setSessions] = useState<CoachSession[]>([]);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [selectedSession, setSelectedSession] = useState<CoachSession | null>(null);

  const weekStart = getWeekStart(currentDate);

  const loadSessions = useCallback(async () => {
    if (viewMode === 'week') {
      const start = getWeekStart(currentDate);
      const result = await getSessionsForWeek(coachId, start);
      setSessions(result);
    } else {
      // Month: fetch all sessions for month by fetching week-by-week or use getSessions
      // Simple approach: fetch the whole month range via getSessionsForWeek spanning ~5 weeks
      const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const weekOfMonth = getWeekStart(monthStart);
      const allSessions: CoachSession[] = [];
      const seen = new Set<string>();
      for (let i = 0; i < 6; i++) {
        const ws = new Date(weekOfMonth.getTime() + i * 7 * 86400000);
        if (ws.getMonth() > currentDate.getMonth() + 1 && ws.getFullYear() >= currentDate.getFullYear()) break;
        const batch = await getSessionsForWeek(coachId, ws);
        for (const s of batch) {
          if (!seen.has(s.id)) { seen.add(s.id); allSessions.push(s); }
        }
      }
      setSessions(allSessions);
    }
  }, [coachId, viewMode, currentDate, refreshKey]);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  // Group sessions by date key
  const sessionsByDate = new Map<string, CoachSession[]>();
  sessions.forEach((s) => {
    const key = s.session_date;
    const arr = sessionsByDate.get(key) || [];
    arr.push(s);
    sessionsByDate.set(key, arr);
  });

  const navigatePrev = () => {
    const d = new Date(currentDate);
    if (viewMode === 'week') d.setDate(d.getDate() - 7);
    else d.setMonth(d.getMonth() - 1);
    setCurrentDate(d);
  };

  const navigateNext = () => {
    const d = new Date(currentDate);
    if (viewMode === 'week') d.setDate(d.getDate() + 7);
    else d.setMonth(d.getMonth() + 1);
    setCurrentDate(d);
  };

  const handleCancelSession = async (session: CoachSession) => {
    Alert.alert('Cancel Session', `Cancel session with ${session.client_name}?`, [
      { text: 'No', style: 'cancel' },
      {
        text: 'Yes, Cancel',
        style: 'destructive',
        onPress: async () => {
          await cancelSession(session.id);
          setSelectedSession(null);
          loadSessions();
        },
      },
    ]);
  };

  const todayStr = formatDateKey(new Date());

  // ── Week View ──
  const renderWeekView = () => {
    const days = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart.getTime() + i * 86400000);
      return { date: d, key: formatDateKey(d) };
    });

    const weekLabel = `${days[0].date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${days[6].date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

    return (
      <>
        <Text style={[styles.rangeLabel, { color: c.textSecondary }]}>{weekLabel}</Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          <View style={styles.weekGrid}>
            {days.map(({ date, key }) => {
              const daySessions = sessionsByDate.get(key) || [];
              const isToday = key === todayStr;
              return (
                <TouchableOpacity
                  key={key}
                  style={[
                    styles.weekColumn,
                    { borderColor: c.border },
                    isToday && { borderColor: c.accent, borderWidth: 2 },
                  ]}
                  onPress={() => setSelectedDay(key)}
                  activeOpacity={0.7}
                >
                  <Text style={[styles.weekDayLabel, { color: isToday ? c.accent : c.textSecondary }]}>
                    {WEEKDAYS[date.getDay()]}
                  </Text>
                  <Text style={[styles.weekDayNum, { color: isToday ? c.accent : c.text }]}>
                    {date.getDate()}
                  </Text>
                  <View style={styles.weekSessionList}>
                    {daySessions.sort((a, b) => a.start_time.localeCompare(b.start_time)).map((s) => (
                      <TouchableOpacity
                        key={s.id}
                        style={[styles.weekSessionChip, { backgroundColor: c.accent + '22' }]}
                        onPress={() => setSelectedSession(s)}
                      >
                        <Text style={[styles.weekSessionTime, { color: c.accent }]} numberOfLines={1}>
                          {formatTime12(s.start_time)}
                        </Text>
                        <Text style={[styles.weekSessionName, { color: c.text }]} numberOfLines={1}>
                          {s.client_name}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                </TouchableOpacity>
              );
            })}
          </View>
        </ScrollView>
      </>
    );
  };

  // ── Month View ──
  const renderMonthView = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const cells: (number | null)[] = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);

    return (
      <>
        <View style={styles.monthWeekRow}>
          {WEEKDAYS.map((d) => (
            <View key={d} style={styles.monthWeekCell}>
              <Text style={[styles.monthWeekLabel, { color: c.textTertiary }]}>{d}</Text>
            </View>
          ))}
        </View>
        <View style={styles.monthGrid}>
          {cells.map((day, idx) => {
            if (day === null) return <View key={`e${idx}`} style={styles.monthDayCell} />;
            const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const count = (sessionsByDate.get(key) || []).length;
            const isToday = key === todayStr;

            return (
              <TouchableOpacity
                key={key}
                style={[
                  styles.monthDayCell,
                  isToday && { borderColor: c.accent, borderWidth: 2, borderRadius: RADIUS.sm },
                ]}
                onPress={() => setSelectedDay(key)}
              >
                <Text style={[styles.monthDayNum, { color: isToday ? c.accent : c.text }]}>{day}</Text>
                {count > 0 && (
                  <View style={[styles.badge, { backgroundColor: c.accent }]}>
                    <Text style={[styles.badgeText, { color: c.accentForeground }]}>{count}</Text>
                  </View>
                )}
              </TouchableOpacity>
            );
          })}
        </View>
      </>
    );
  };

  // ── Day Detail Modal ──
  const renderDayDetail = () => {
    if (!selectedDay) return null;
    const daySessions = (sessionsByDate.get(selectedDay) || [])
      .sort((a, b) => a.start_time.localeCompare(b.start_time));
    const dayDate = new Date(selectedDay + 'T12:00:00');

    return (
      <Modal visible transparent animationType="fade" onRequestClose={() => setSelectedDay(null)}>
        <Pressable style={styles.overlay} onPress={() => setSelectedDay(null)}>
          <View style={[styles.detailSheet, { backgroundColor: c.surface, borderColor: c.border }]}>
            <View style={styles.detailHeader}>
              <Text style={[styles.detailTitle, { color: c.text }]}>
                {dayDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
              </Text>
              <TouchableOpacity onPress={() => setSelectedDay(null)}>
                <X size={18} color={c.textSecondary} />
              </TouchableOpacity>
            </View>
            {daySessions.length === 0 ? (
              <Text style={[styles.noSessions, { color: c.textTertiary }]}>No sessions scheduled</Text>
            ) : (
              <ScrollView style={{ maxHeight: 300 }}>
                {daySessions.map((s) => (
                  <TouchableOpacity
                    key={s.id}
                    style={[styles.sessionRow, { borderColor: c.border }]}
                    onPress={() => { setSelectedDay(null); setSelectedSession(s); }}
                  >
                    <View style={{ flex: 1, gap: 2 }}>
                      <Text style={[styles.sessionClient, { color: c.text }]}>{s.client_name}</Text>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                        <Clock size={12} color={c.textSecondary} />
                        <Text style={[styles.sessionMeta, { color: c.textSecondary }]}>
                          {formatTime12(s.start_time)} · {s.duration_minutes}min
                        </Text>
                      </View>
                    </View>
                    <ChevronRight size={16} color={c.textTertiary} />
                  </TouchableOpacity>
                ))}
              </ScrollView>
            )}
          </View>
        </Pressable>
      </Modal>
    );
  };

  // ── Session Detail Modal ──
  const renderSessionDetail = () => {
    if (!selectedSession) return null;
    const s = selectedSession;
    return (
      <Modal visible transparent animationType="fade" onRequestClose={() => setSelectedSession(null)}>
        <Pressable style={styles.overlay} onPress={() => setSelectedSession(null)}>
          <View style={[styles.detailSheet, { backgroundColor: c.surface, borderColor: c.border }]}>
            <View style={styles.detailHeader}>
              <Text style={[styles.detailTitle, { color: c.text }]}>Session Details</Text>
              <TouchableOpacity onPress={() => setSelectedSession(null)}>
                <X size={18} color={c.textSecondary} />
              </TouchableOpacity>
            </View>
            <View style={{ gap: SPACE['3'] }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <User size={16} color={c.accent} />
                <Text style={[styles.sessionDetailLabel, { color: c.text }]}>{s.client_name}</Text>
              </View>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <CalendarDays size={16} color={c.accent} />
                <Text style={[styles.sessionDetailLabel, { color: c.text }]}>
                  {new Date(s.session_date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                </Text>
              </View>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <Clock size={16} color={c.accent} />
                <Text style={[styles.sessionDetailLabel, { color: c.text }]}>
                  {formatTime12(s.start_time)} · {s.duration_minutes} min
                </Text>
              </View>
              {s.notes ? (
                <Text style={[styles.sessionNotes, { color: c.textSecondary }]}>{s.notes}</Text>
              ) : null}
            </View>
            <View style={styles.sessionActions}>
              <TouchableOpacity
                style={[styles.actionButton, { borderColor: c.border }]}
                onPress={() => {
                  setSelectedSession(null);
                  onEditSession?.(s);
                }}
              >
                <Edit3 size={14} color={c.accent} />
                <Text style={[styles.actionBtnLabel, { color: c.accent }]}>Edit</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.actionButton, { borderColor: c.error }]}
                onPress={() => handleCancelSession(s)}
              >
                <Trash2 size={14} color={c.error} />
                <Text style={[styles.actionBtnLabel, { color: c.error }]}>Cancel</Text>
              </TouchableOpacity>
            </View>
          </View>
        </Pressable>
      </Modal>
    );
  };

  return (
    <View style={styles.container}>
      {/* Header: nav + view toggle */}
      <View style={styles.header}>
        <TouchableOpacity onPress={navigatePrev} hitSlop={12}>
          <ChevronLeft size={24} color={c.text} />
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: c.text }]}>
          {viewMode === 'month'
            ? `${MONTH_NAMES[currentDate.getMonth()]} ${currentDate.getFullYear()}`
            : `${MONTH_NAMES[currentDate.getMonth()]} ${currentDate.getFullYear()}`}
        </Text>
        <TouchableOpacity onPress={navigateNext} hitSlop={12}>
          <ChevronRight size={24} color={c.text} />
        </TouchableOpacity>
      </View>

      {/* View Toggle */}
      <View style={[styles.toggleRow, { borderColor: c.border }]}>
        <TouchableOpacity
          style={[styles.toggleBtn, viewMode === 'week' && { backgroundColor: c.accent }]}
          onPress={() => setViewMode('week')}
        >
          <CalendarDays size={14} color={viewMode === 'week' ? c.accentForeground : c.textSecondary} />
          <Text style={[styles.toggleText, { color: viewMode === 'week' ? c.accentForeground : c.textSecondary }]}>
            Week
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.toggleBtn, viewMode === 'month' && { backgroundColor: c.accent }]}
          onPress={() => setViewMode('month')}
        >
          <LayoutGrid size={14} color={viewMode === 'month' ? c.accentForeground : c.textSecondary} />
          <Text style={[styles.toggleText, { color: viewMode === 'month' ? c.accentForeground : c.textSecondary }]}>
            Month
          </Text>
        </TouchableOpacity>
      </View>

      {/* Calendar */}
      {viewMode === 'week' ? renderWeekView() : renderMonthView()}

      {/* Modals */}
      {renderDayDetail()}
      {renderSessionDetail()}
    </View>
  );
}

const SCREEN_WIDTH = Dimensions.get('window').width;
const WEEK_COL_WIDTH = Math.floor((SCREEN_WIDTH - 48) / 7);
const MONTH_CELL = Math.floor((SCREEN_WIDTH - 32) / 7);

const styles = StyleSheet.create({
  container: { flex: 1 },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: SPACE['2'],
    marginBottom: SPACE['2'],
  },
  headerTitle: {
    fontFamily: TYPO.heading.bold,
    fontSize: FONT.xl,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  toggleRow: {
    flexDirection: 'row',
    alignSelf: 'center',
    borderWidth: 1,
    borderRadius: RADIUS.md,
    marginBottom: SPACE['4'],
    overflow: 'hidden',
  },
  toggleBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: SPACE['4'],
    paddingVertical: SPACE['2'],
  },
  toggleText: {
    fontFamily: TYPO.body.semibold,
    fontSize: FONT.sm,
  },
  rangeLabel: {
    fontFamily: TYPO.body.medium,
    fontSize: FONT.sm,
    textAlign: 'center',
    marginBottom: SPACE['2'],
  },
  // Week view
  weekGrid: {
    flexDirection: 'row',
    paddingHorizontal: SPACE['2'],
    gap: SPACE['1'],
  },
  weekColumn: {
    width: WEEK_COL_WIDTH,
    minHeight: 160,
    borderWidth: 1,
    borderRadius: RADIUS.md,
    padding: SPACE['1'],
    alignItems: 'center',
  },
  weekDayLabel: {
    fontFamily: TYPO.body.medium,
    fontSize: FONT.xs,
    textTransform: 'uppercase',
  },
  weekDayNum: {
    fontFamily: TYPO.heading.bold,
    fontSize: FONT.lg,
    marginBottom: SPACE['1'],
  },
  weekSessionList: {
    width: '100%',
    gap: 3,
  },
  weekSessionChip: {
    borderRadius: RADIUS.sm,
    padding: 3,
  },
  weekSessionTime: {
    fontFamily: TYPO.body.semibold,
    fontSize: 8,
  },
  weekSessionName: {
    fontFamily: TYPO.body.regular,
    fontSize: 8,
  },
  // Month view
  monthWeekRow: {
    flexDirection: 'row',
    marginBottom: SPACE['1'],
  },
  monthWeekCell: {
    width: MONTH_CELL,
    alignItems: 'center',
  },
  monthWeekLabel: {
    fontFamily: TYPO.body.medium,
    fontSize: FONT.xs,
    textTransform: 'uppercase',
  },
  monthGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  monthDayCell: {
    width: MONTH_CELL,
    height: MONTH_CELL,
    alignItems: 'center',
    justifyContent: 'center',
  },
  monthDayNum: {
    fontFamily: TYPO.body.regular,
    fontSize: FONT.sm,
  },
  badge: {
    borderRadius: RADIUS.full,
    minWidth: 16,
    height: 16,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 2,
    paddingHorizontal: 4,
  },
  badgeText: {
    fontFamily: TYPO.body.bold,
    fontSize: 11,
  },
  // Modals
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  detailSheet: {
    width: '85%',
    maxWidth: 360,
    borderRadius: RADIUS.lg,
    borderWidth: 1,
    padding: SPACE['6'],
  },
  detailHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: SPACE['4'],
  },
  detailTitle: {
    fontFamily: TYPO.heading.bold,
    fontSize: FONT.lg,
  },
  noSessions: {
    fontFamily: TYPO.body.regular,
    fontSize: FONT.base,
    textAlign: 'center',
    paddingVertical: SPACE['4'],
  },
  sessionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: SPACE['3'],
    borderBottomWidth: 1,
  },
  sessionClient: {
    fontFamily: TYPO.body.bold,
    fontSize: FONT.base,
  },
  sessionMeta: {
    fontFamily: TYPO.body.regular,
    fontSize: FONT.sm,
  },
  sessionDetailLabel: {
    fontFamily: TYPO.body.semibold,
    fontSize: FONT.base,
  },
  sessionNotes: {
    fontFamily: TYPO.body.regular,
    fontSize: FONT.sm,
    marginTop: SPACE['1'],
    fontStyle: 'italic',
  },
  sessionActions: {
    flexDirection: 'row',
    gap: SPACE['3'],
    marginTop: SPACE['5'],
  },
  actionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    borderWidth: 1,
    borderRadius: RADIUS.md,
    paddingVertical: SPACE['2'],
  },
  actionBtnLabel: {
    fontFamily: TYPO.body.bold,
    fontSize: FONT.sm,
  },
});
